import { useEffect, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ShieldCheck } from 'lucide-react';
import {
  securityCodeDetailsFor,
  thisDeviceQuantumSafe,
} from '../../lib/e2ee/session';
import { useT } from '../../i18n';

interface Person {
  id: string;
  name: string;
}

function CodeBlock({ label, code }: { label: string; code: string | null }) {
  const t = useT();
  return (
    <Box bg="surface.subtle" borderRadius="18px" p={4}>
      <Text fontSize="14px" fontWeight={700} color="muted" pb={1}>
        {label}
      </Text>
      {code ? (
        <Text
          fontFamily="mono"
          fontSize="20px"
          letterSpacing="0.04em"
          lineHeight="30px"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {code}
        </Text>
      ) : (
        <Text color="muted">{t('chat.security.noDevices')}</Text>
      )}
    </Box>
  );
}

// Security codes (M28, D63): each person's code comes from their devices'
// public keys. If both people see the same two codes, no extra device has
// been slipped into the conversation.
export function SecurityCodeDialog({
  me,
  them,
  isOpen,
  onClose,
}: {
  me: Person;
  them: Person | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [codes, setCodes] = useState<{
    mine: string | null;
    theirs: string | null;
    /** Either side has a device without an ML-KEM key (M78, D338). */
    needsLatest: boolean;
    /** Their devices the server marks as needing an update (§6.5). */
    theirsOutdated: number;
  } | null>(null);
  const t = useT();

  useEffect(() => {
    const themId = them?.id;
    if (!isOpen || !themId) return;
    let cancelled = false;
    setCodes(null);
    // From this device's record (D302): the devices it would wrap for, any
    // new one learned (and announced, D303) first.
    Promise.all([
      securityCodeDetailsFor(me.id),
      securityCodeDetailsFor(themId),
    ])
      .then(([mine, theirs]) => {
        if (!cancelled) {
          setCodes({
            mine: mine.code,
            theirs: theirs.code,
            needsLatest: !mine.allPq || !theirs.allPq,
            theirsOutdated: theirs.needsUpdate,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCodes({
            mine: null,
            theirs: null,
            needsLatest: false,
            theirsOutdated: 0,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, me.id, them?.id]);

  const first = them?.name.split(' ')[0] ?? t('chat.security.them');
  // The small mark while both devices' keys are fetched (M66, D250).
  const codesState = useLoadingState({ loading: isOpen && codes === null });
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={4} borderRadius="28px">
        <ModalBody pt={6}>
          <Stack spacing={4}>
            <Stack spacing={2} align="center" textAlign="center">
              <Box color="accent">
                <ShieldCheck size={36} aria-hidden="true" />
              </Box>
              <Heading as="h2" fontSize="24px">
                {t('chat.header.securityCodes')}
              </Heading>
              <Text color="ink.body">
                {t('chat.security.body', { name: first })}
              </Text>
            </Stack>
            {codes ? (
              <>
                <CodeBlock label={t('chat.security.yours')} code={codes.mine} />
                <CodeBlock
                  label={t('chat.security.theirs', { name: first })}
                  code={codes.theirs}
                />
              </>
            ) : (
              <LoadingPanel state={codesState} size="inline" />
            )}
            <Text fontSize="14px" color="muted">
              {t('chat.security.changes')}
            </Text>
            {codes ? (
              <Text fontSize="14px" color="muted">
                {thisDeviceQuantumSafe()
                  ? t('chat.security.thisDeviceSafe')
                  : t('chat.security.thisDeviceNotSafe')}
              </Text>
            ) : null}
            {codes?.needsLatest ? (
              <Text fontSize="14px" color="muted">
                {t('chat.codesNeedUpdate')}
              </Text>
            ) : null}
            {codes && codes.theirsOutdated > 0 ? (
              <Text fontSize="14px" color="muted">
                {t('chat.security.deviceNeedsUpdate', { name: first })}
              </Text>
            ) : null}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button w="full" h="48px" onClick={onClose}>
            {t('common.done')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
